import {Optional} from '@othree.io/optional'
import { DockerImageAsset } from 'aws-cdk-lib/aws-ecr-assets'
import {PolicyStatement, Role, ServicePrincipal} from 'aws-cdk-lib/aws-iam'
import {IVpc} from 'aws-cdk-lib/aws-ec2'
import {Stack} from 'aws-cdk-lib'
import {AwsLogDriver, Cluster, ContainerImage, FargateService, FargateTaskDefinition} from 'aws-cdk-lib/aws-ecs'

export type FargateTaskInput = Readonly<{
    name: string
    image: DockerImageAsset
    environment: Record<string, string>
    policies: Array<PolicyStatement>
    vpc?: IVpc
    taskCount: number
}>

export type FargateTaskConfiguration = Readonly<{
    memoryLimitMiB: number
    cpu: number
    publicIp: boolean
}>


/**
 * Creates an ECS Fargate service with a cluster, task role, container, and CloudWatch logging.
 */
export const withFargateService = (deps: {
    stack: Stack
    defaultConfiguration: FargateTaskConfiguration
}) =>
    (input: FargateTaskInput, configuration?: Partial<FargateTaskConfiguration>): FargateService => {
        const cluster = new Cluster(deps.stack, `${input.name}-Cluster`, {
            clusterName: `${input.name}-Cluster`,
            vpc: input.vpc
        })

        const role = new Role(deps.stack, `${input.name}-Role`, {
            roleName: `${input.name}-Role`,
            assumedBy: new ServicePrincipal('ecs-tasks.amazonaws.com')
        })
        input.policies.forEach(policy => {
            role.addToPolicy(policy)
        })

        const task = new FargateTaskDefinition(deps.stack, `${input.name}-Task`, {
            ...deps.defaultConfiguration,
            ...Optional(configuration).orElse({}),
            taskRole: role
        })

        task.addContainer(`${input.name}-Container`, {
            image: ContainerImage.fromEcrRepository(input.image.repository, input.image.assetHash),
            environment: input.environment,
            logging: new AwsLogDriver({
                streamPrefix: input.name
            })
        })

        const service = new FargateService(deps.stack, `${input.name}-Service`, {
            cluster: cluster,
            taskDefinition: task,
            assignPublicIp: Optional(configuration).map(_ => _.publicIp).orElse(deps.defaultConfiguration.publicIp),
            desiredCount: input.taskCount
        })

        return service
    }
